/**
 * Milk split — Settings > My-School, the STRAIGHT SERVE counterpart of the
 * Pre-fill settings card's nested milk rows.
 *
 * Since PR #2153/#2166, SS campuses derive system milk automatically: one
 * carton per meal, apportioned across the lineup SKUs by `MilkShareRate`
 * rows (even split when none are configured), with per-day count overrides
 * in the order sheet's Milk dropdown. What was missing is the SETTINGS
 * surface to configure the default % split (e.g. 95% Whole / 5%
 * Lactose-Free) — the OVS card carries it, but only renders for campuses
 * with a by-component order column, so SS campuses had no surface at all
 * (Sheila, 2026-08-24: "expose the 'configure %' for Straight Serve schools
 * in settings").
 *
 * Deliberately COMPACT: just the per-SKU percentage editor, per mealtime,
 * mirroring the OVS card's sub-card + Edit-dialog layout and its
 * inherited-vs-override cascade (the grid pieces are literally shared —
 * `milk-share-grid.tsx`). None of the component-split machinery renders
 * here: SS campuses always ship 100% of every component, so the split
 * toggle, kind rows, and weekday rules have no meaning for them.
 *
 * Gating (owned by `pages/settings.tsx`): exactly ONE milk editor per
 * campus. OVS campuses (`anyColumnOrdersByComponent`) keep the Pre-fill
 * card, which already contains the milk rows; only non-OVS campuses get
 * this card. Within that, the card renders only when the campus's milk
 * lineup is non-empty — a campus whose menus carry no milk has nothing to
 * split, so it renders nothing rather than an empty editor.
 *
 * Rate changes do NOT reach orders that have already been placed. The card
 * says so where the user edits, mirroring the OVS card.
 */

import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { Milk, Pencil } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

import { type MilkProfile, useMilkSharesQuery } from '@/api/location-settings'
import type { AdminSchool } from '@/api/school-orders'
import { type Mealtime, MEALTIME_LABELS, sortMealtimes } from '@/lib/mealtime'
import { buildOrderColumns } from '@/lib/school-orders/columns'
import { evenSplitPct, rateRowColumnKey } from '@/lib/school-orders/milk'
import { formatMilkShareSummary, resolveShareCells } from '@/lib/school-orders/split-provenance'

import { CardTitleRow } from './campus-scope'
import { columnCellKey, MilkShareGrid } from './milk-share-grid'
import { SplitEditDialog } from './split-edit-dialog'
import { useSplitEditor } from './split-editor'

export interface MilkSplitSettingsCardProps {
  school: AdminSchool
  /**
   * The section's campus picker (`CampusScope`), rendered in this card's
   * header. Absent for a single-campus org — then this card is exactly what
   * it was before per-card scoping, including rendering NOTHING when the
   * campus has no milk lineup. With a picker present an empty lineup renders
   * the header + picker + a one-line explanation instead of nothing, because
   * a card that vanishes takes the only way back to the other campuses with
   * it.
   */
  scopeControl?: ReactNode
}

export function MilkSplitSettingsCard({ school, scopeControl }: MilkSplitSettingsCardProps) {
  const locationId = school.id
  const columns = useMemo(() => buildOrderColumns(school), [school])
  const milkSharesQuery = useMilkSharesQuery(locationId)
  const [editingMealtime, setEditingMealtime] = useState<Mealtime | null>(null)
  // Cells stage their edits here; the dialog's Save button writes them.
  const editor = useSplitEditor(locationId)

  const shares = useMemo(() => milkSharesQuery.data?.shares ?? [], [milkSharesQuery.data])
  // The DISTRICT's own rows. Rendered as input PLACEHOLDERS only — never as
  // values. See `split-provenance.ts`: persisting a displayed inherited value
  // detaches this campus from the district permanently.
  const inheritedShares = useMemo(() => milkSharesQuery.data?.inheritedShares ?? [], [milkSharesQuery.data])
  const milkSkus = useMemo(() => milkSharesQuery.data?.milkSkus ?? [], [milkSharesQuery.data])
  const milkProfile: MilkProfile = milkSharesQuery.data?.milkProfile ?? { milkOptionCodes: [], noMilkMode: false }

  // One row per served mealtime, plus a row for any mealtime that already has
  // scoped share rows but dropped out of the served list — existing config
  // must never become invisible (mirrors the OVS card's `cardMealtimes`). An
  // unprovisioned campus with no scoped rows falls back to a single Lunch row
  // so the split stays editable.
  const cardMealtimes = useMemo(() => {
    const served = school.servedMealtimes ?? []
    const stale = new Set<Mealtime>()
    for (const row of shares) {
      if (row.mealtime && !served.includes(row.mealtime)) {
        stale.add(row.mealtime)
      }
    }
    const all = [...served, ...sortMealtimes(stale)]
    return all.length > 0 ? all : (['LUNCH'] as Mealtime[])
  }, [school.servedMealtimes, shares])
  const servedSet = useMemo(() => new Set(school.servedMealtimes ?? []), [school.servedMealtimes])

  // ONE header title row, reused by every render branch below — the campus
  // picker must be present in all of them (see `campus-scope.tsx`).
  const titleRow = (
    <CardTitleRow scopeControl={scopeControl}>
      <CardTitle className="flex items-center gap-2 text-base">
        <Milk className="h-4 w-4" />
        Milk split
      </CardTitle>
    </CardTitleRow>
  )

  if (milkSharesQuery.isPending) {
    return (
      <Card>
        <CardHeader className="pb-3">{titleRow}</CardHeader>
        <CardContent className="flex flex-col gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }
  if (milkSharesQuery.error) {
    return (
      <Card>
        {/* Header here ONLY so the campus picker survives a failed load; a
            single-campus org keeps the original bare error card. */}
        {scopeControl ? <CardHeader className="pb-3">{titleRow}</CardHeader> : null}
        <CardContent className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
          Couldn't load the milk split. {milkSharesQuery.error.message}
        </CardContent>
      </Card>
    )
  }
  // No milk in the campus's lineup → nothing to split → no card. NOT an empty
  // state: a campus whose menus never carry milk shouldn't see a milk setting.
  if (milkSkus.length === 0) {
    // ...unless this card carries the section's campus picker: dropping the
    // whole card would drop the only control for reaching the campuses that
    // DO have milk.
    if (!scopeControl) {
      return null
    }
    return (
      <Card>
        <CardHeader className="pb-3">{titleRow}</CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {school.name}&rsquo;s menus don&rsquo;t include milk, so there&rsquo;s no split to set here.
          </p>
        </CardContent>
      </Card>
    )
  }

  const skuIds = milkSkus.map((sku) => sku.id)
  const evenBpsBySku = evenSplitPct(milkSkus.length).map((pct) => pct * 100)

  /** The resolved per-column share sets for one mealtime, first column first. */
  const setsFor = (mealtime: Mealtime) =>
    columns.map((col) =>
      resolveShareCells({
        ownRows: shares,
        inheritedRows: inheritedShares,
        skuIds,
        mealtime,
        columnKey: columnCellKey(col),
        columnKeyOf: rateRowColumnKey,
        evenBps: evenBpsBySku,
      }),
    )

  return (
    <Card>
      <CardHeader className="pb-3">
        {titleRow}
        <p className="text-sm text-muted-foreground">
          Your campus ships one milk per meal. These percentages decide how those cartons divide across your milk types.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {cardMealtimes.map((m) => (
          <div key={m} className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="min-w-0">
              <h4 className="text-sm font-medium">
                {MEALTIME_LABELS[m]}
                {servedSet.has(m) || cardMealtimes.length === 1 ? null : (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">(not served)</span>
                )}
              </h4>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {formatMilkShareSummary(milkSkus, setsFor(m))}
              </p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditingMealtime(m)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          </div>
        ))}

        <p className="mt-1 text-xs text-muted-foreground">
          Changing these percentages affects orders placed from now on. Orders already placed keep the split they were
          placed with.
        </p>
      </CardContent>

      <SplitEditDialog
        open={editingMealtime !== null}
        onClose={() => setEditingMealtime(null)}
        editor={editor}
        title={`Milk split — ${editingMealtime ? MEALTIME_LABELS[editingMealtime] : ''}`}
        description={
          <>
            Each milk&rsquo;s share of the cartons shipped at{' '}
            {editingMealtime ? MEALTIME_LABELS[editingMealtime].toLowerCase() : ''}. Every box is editable; nothing is
            written until you press <span className="font-medium text-foreground">Save changes</span>. A cell marked
            &ldquo;District&rdquo; is inheriting that number — type a different one to override it here, and the reset
            arrow puts it back. Saving applies to orders placed from now on; orders already placed keep the split they
            were placed with.
          </>
        }>
        {editingMealtime ? (
          <MilkShareGrid
            locationId={locationId}
            columns={columns}
            mealtime={editingMealtime}
            milkSkus={milkSkus}
            milkProfile={milkProfile}
            shares={shares}
            inheritedShares={inheritedShares}
            editor={editor}
          />
        ) : null}
      </SplitEditDialog>
    </Card>
  )
}
